import React from "react";
import { IconButton, Snackbar } from "@mui/material";
import { ContentCopy as CopyOutlined } from "@mui/icons-material";
import { styled } from "@mui/material";

const CodeBlockWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  "&:hover .copy-button": {
    opacity: 1,
  },
}));

const CopyButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "10px",
  top: "10px",
  opacity: 0,
  transition: "opacity 0.2s",
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
  color: theme.palette.mode === "dark" ? "#fff" : "#000",
  padding: "4px",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
  },
}));

const Pre = styled("pre")(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#2d2d2d" : "#f8f9fa",
  borderRadius: "8px",
  padding: "20px",
  margin: "1.5em 0",
  border: `1px solid ${theme.palette.mode === "dark" ? "#404040" : "#e0e0e0"}`,
  overflowX: "auto",
  maxHeight: "500px",
  overflowY: "auto",
  userSelect: "text",
  cursor: "text",
  fontSize: "0.9em",
  fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',

  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.mode === "dark" ? "#404040" : "#888",
    borderRadius: "4px",
    "&:hover": {
      background: theme.palette.mode === "dark" ? "#505050" : "#555",
    },
  },
}));

interface ChatCodeBlockProps {
  children: React.ReactNode;
}

export const ChatCodeBlock: React.FC<ChatCodeBlockProps> = ({ children }) => {
  const [showCopyNotification, setShowCopyNotification] = React.useState(false);

  const handleCopy = async () => {
    if (typeof children === "string") {
      await navigator.clipboard.writeText(children);
      setShowCopyNotification(true);
    }
  };

  return (
    <CodeBlockWrapper>
      <Pre>
        {children}
        <CopyButton
          className="copy-button"
          onClick={handleCopy}
          size="small"
          aria-label="copy code"
        >
          <CopyOutlined />
        </CopyButton>
      </Pre>
      <Snackbar
        open={showCopyNotification}
        autoHideDuration={2000}
        onClose={() => setShowCopyNotification(false)}
        message="Code copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </CodeBlockWrapper>
  );
};
